import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import { Card, Grid, Icon } from "@mui/material";
import ElectricMeterIcon from '@mui/icons-material/ElectricMeter';
import BoltIcon from '@mui/icons-material/Bolt';
import SpeedIcon from '@mui/icons-material/Speed';
import CompressIcon from '@mui/icons-material/Compress';
import WaterIcon from '@mui/icons-material/Water';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { RequestService } from "services/RequestService";

function MetricView({ powerStationId }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const response = await RequestService.get(
        `PowerStation/metric?Date=${today}&PowerStationId=${powerStationId}`
      );
      if (response.data && !response.data.error) {
        setMetrics(response.data.data);
        setError(null);
      } else {
        setError(response.data.message || "حدث خطأ في جلب البيانات");
      }
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setError("حدث خطأ في جلب البيانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (powerStationId) {
      fetchMetrics();
      // Refresh metrics every minute
      const interval = setInterval(fetchMetrics, 60000);
      return () => clearInterval(interval);
    }
  }, [powerStationId]);

  const MetricCard = ({ title, value, unit, icon, color, iconComponent: IconComponent }) => (
    <Card sx={{ height: '100%' }}>
      <MDBox p={2}>
        <Grid container spacing={1}>
          <Grid item xs={8}>
            <MDBox>
              <MDTypography variant="button" fontWeight="medium" color="text">
                {title}
              </MDTypography>
            </MDBox>
            <MDBox display="flex" alignItems="center">
              <MDTypography variant="h5" fontWeight="bold">
                {typeof value === 'number' ? value.toFixed(2) : '-'} {unit}
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={4}>
            <MDBox
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              height="100%"
            >
              {IconComponent ? (
                <IconComponent sx={{ fontSize: 40, color }} />
              ) : (
                <Icon sx={{ fontSize: 40, color }}>{icon}</Icon>
              )}
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );

  MetricCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.number,
    unit: PropTypes.string.isRequired,
    icon: PropTypes.string,
    color: PropTypes.string.isRequired,
    iconComponent: PropTypes.elementType,
  };

  MetricCard.defaultProps = {
    value: null,
    icon: "",
    iconComponent: null,
  };

  if (loading) {
    return (
      <MDBox textAlign="center" py={2}>
        <MDTypography>جاري تحميل البيانات...</MDTypography>
      </MDBox>
    );
  }

  if (error) {
    return (
      <MDBox textAlign="center" py={2}>
        <MDTypography color="error">{error}</MDTypography>
      </MDBox>
    );
  }

  if (!metrics) return null;

  return (
    <MDBox mt={3}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="السعة"
            value={metrics.capacity}
            unit="MW"
            color="#1A73E8"
            iconComponent={ElectricMeterIcon}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="الطاقة المولدة"
            value={metrics.powerGenerator}
            unit="MW"
            color="#4CAF50"
            iconComponent={BoltIcon}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="المستهدف"
            value={metrics.target}
            unit="MW"
            color="#FB8C00"
            iconComponent={PrecisionManufacturingIcon}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="ضغط الغاز"
            value={metrics.gasPressure}
            unit="bar"
            color="#E53935"
            iconComponent={CompressIcon}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="تدفق الغاز (م³/ساعة)"
            value={metrics.gasVolume}
            unit="m³"
            color="#8E24AA"
            iconComponent={WaterIcon}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="التردد"
            value={metrics.frequency}
            unit="Hz"
            color="#00ACC1"
            iconComponent={SpeedIcon}
          />
        </Grid>
      </Grid>
    </MDBox>
  );
}

MetricView.propTypes = {
  powerStationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default MetricView;
